<template>
  <div v-if="!isHcpView" id="lesson-scroller">
    <div class="content">
      <div class="ls-header">
        <div v-if="$store.state.status.payment.paid" class="ls-header-text ellipsis">
          <span v-if="!isLoading && currentLessonObject">{{ $t('message.lessonScrollerWeekPrefix') }}</span>
          <strong>
            <router-link v-if="currentLessonObject" :to="'lesson/' + currentLessonObject.permalink">
              <span
                class="bold"
                v-text="!isLoading ? ' ' + currentLessonObject.week_name + ': ' + currentLessonObject.title : ''"
              ></span>
            </router-link>
          </strong>
          <span v-text="isLoading ? $t('message.loadingText') : ''"></span>
        </div>
        <div class="ls-header-nav">
          <button class="ls-nav-btn" type="button" :disabled="isLeftLocked || isLoading" @click="goLeft">
            <i class="material-icons">chevron_left</i>
          </button>
          <button class="ls-nav-btn" type="button" :disabled="isRightLocked || isLoading" @click="goRight">
            <i class="material-icons">chevron_right</i>
          </button>
        </div>
      </div>
    </div>

    <div ref="rail" class="ls-rail">
      <template v-for="tile in tiles">
        <!-- Loading placeholders -->
        <div v-if="tile.kind === 'loader'" :key="'loader-' + tile.key" class="ls-tile dummyTile">
          <div class="ls-titlebar">
            <div class="ls-titles">
              <div class="ls-loader-bar wide"></div>
              <div class="ls-loader-bar"></div>
            </div>
          </div>
        </div>

        <!-- Lesson tile -->
        <div
          v-else-if="tile.kind === 'lesson'"
          :key="'lesson-' + tile.key"
          :data-lesson="tile.lessonNumber"
          class="ls-tile outerItem"
          :class="{ locked: tile.locked }"
          @click="goToPermalink(tile.data)"
        >
          <span v-if="tile.data.hasPersonalization" class="ls-chip ls-chip--personalized">{{ $t('message.labelPersonalized') }}</span>
          <img :src="tile.data.image_url" :alt="tile.data.title" />
          <div class="ls-titlebar">
            <div class="ls-titles">
              <div class="ls-title" v-text="tile.data.title"></div>
              <div class="ls-subtitle" v-text="tile.data.week_name.replace('Week', $t('message.labelWeek'))"></div>
            </div>
            <button class="ls-action" type="button">
              <i class="material-icons">{{ tile.locked ? 'lock' : 'play_arrow' }}</i>
            </button>
          </div>
        </div>

        <!-- Bonus module tile -->
        <div
          v-else
          :key="'bonus-' + tile.key"
          class="ls-tile innerItem"
          :class="{ locked: tile.locked }"
          @click="goToBonus(tile.data, tile.parent)"
        >
          <span class="ls-chip ls-chip--bonus">{{ $t('message.labelBonus') }}</span>
          <img :src="tile.data.image_url" />
          <div class="ls-titlebar">
            <div class="ls-titles">
              <div class="ls-title" v-text="tile.data.title"></div>
              <div class="ls-subtitle" v-text="tile.data.subTitle.replace('Week', $t('message.labelWeek'))"></div>
            </div>
            <button class="ls-action" type="button">
              <i class="material-icons">{{ tile.locked ? 'lock' : 'play_arrow' }}</i>
            </button>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
<script>
  import LessonsApi from '../../../api/Lessons';
  import HCPMixin from "../../../mixins/HCPMixin";

  export default {
    mixins: [HCPMixin],
    name: "lesson-inline-grid",
    data() {
      return {
        isLoading: true,
        activeLesson: 1,
        dummyLoaders: 10,
        list: []
      }
    },
    mounted() {
      let self = this;
      if (self.$store.state.lessons.length > 0) {
        self.setList(self.$store.state.lessons);
      } else {
        LessonsApi.getLessonsList().then(function (response) {
          self.$store.state.lessons = response.data;
          self.setList(response.data);
        });
      }
    },
    methods: {
      setList(lessons) {
        let self = this;
        self.list = lessons;
        self.isLoading = false;
        // Wait for the real tiles to render before measuring/scrolling.
        self.$nextTick(function () {
          self.activeLesson = self.latestUnlockedLesson();
          self.scrollToLesson(self.activeLesson);
        });
      },
      latestUnlockedLesson() {
        let self = this;
        let unlocked = self.list.filter(function (lesson) {
          return lesson.status.locked === false;
        }).length;
        return unlocked > 0 ? unlocked : 1;
      },
      goLeft() {
        this.animateToLesson(this.activeLesson - 1);
      },
      goRight() {
        this.animateToLesson(this.activeLesson + 1);
      },
      animateToLesson(lessonNumber) {
        let self = this;
        self.activeLesson = Math.min(Math.max(lessonNumber, 1), self.list.length);
        self.scrollToLesson(self.activeLesson);
      },
      scrollToLesson(lessonNumber) {
        let self = this;
        let rail = self.$refs.rail;
        if (!rail) {
          return;
        }
        let tile = rail.querySelector('[data-lesson="' + lessonNumber + '"]');
        if (!tile) {
          return;
        }
        let left = tile.getBoundingClientRect().left - rail.getBoundingClientRect().left + rail.scrollLeft;
        rail.scrollTo({ left: left, behavior: 'auto' });
      },
      goToBonus(bonus, tile) {
        let self = this;
        if (self.$store.state.status.payment.paid === true) {
          if (tile.status.locked !== true) {
            self.$router.push({
              name: 'LessonBonus',
              params: {
                id: bonus.id
              }
            });
          }
        } else {
          self.triggerPaywall();
        }
      },
      goToPermalink(tile) {
        let self = this;
        if (self.$store.state.status.payment.paid === true) {
          if (tile.status.locked !== true) {
            self.$router.push({
              name: 'Lesson',
              params: {
                permalink: tile.permalink
              }
            });
          }
        } else {
          self.triggerPaywall();
        }
      },
      triggerPaywall() {
        this.$EventBus.$emit('App::triggerPaywall', true);
      }
    },
    computed: {
      /**
       * Flat list of tiles for the rail: loading placeholders while loading,
       * otherwise each lesson followed by its bonus modules. Replaces the old
       * jQuery DOM surgery that moved nested bonus tiles into the rail.
       */
      tiles() {
        let self = this;
        if (self.isLoading) {
          return Array.from({ length: self.dummyLoaders }, function (value, index) {
            return { kind: 'loader', key: index };
          });
        }

        let paid = self.$store.state.status.payment.paid;
        let out = [];
        self.list.forEach(function (lesson, index) {
          let locked = lesson.status.locked === true || !paid;
          out.push({
            kind: 'lesson',
            key: index,
            lessonNumber: index + 1,
            locked: locked,
            data: lesson
          });
          (lesson.bonus_modules || []).forEach(function (bonus, bonusIndex) {
            out.push({
              kind: 'bonus',
              key: index + '-' + bonusIndex,
              locked: locked,
              data: bonus,
              parent: lesson
            });
          });
        });
        return out;
      },
      isLeftLocked() {
        return this.activeLesson <= 1;
      },
      isRightLocked() {
        return this.activeLesson >= this.list.length;
      },
      currentLessonObject() {
        let self = this;
        if (self.list.length > 0) {
          let items = self.list.filter(function (lesson) {
            return lesson.status.locked === false;
          });
          if (items.length > 0) {
            return items[items.length - 1];
          }
        }
        return false;
      }
    }
  }
</script>
<style lang="less">
  @import "./lesson-inline-grid.less";
</style>



// WEBPACK FOOTER //
// LessonInlineGrid.vue?b6f0f9ba